import {Component, OnDestroy, OnInit} from '@angular/core';
import {GameLevelService} from '../../../services/games/game-level/game-level.service';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {LangSrcService} from '../../../services/lang-src/lang-src.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {GamesSectionSkillsComponent} from '../games-section-skills/games-section-skills.component';
import {PageService} from '../../../services/page/page.service';
import {MetaService} from '../../../services/meta/meta.service';
import {TranslateService} from '@ngx-translate/core';
import {LangTgtService} from '../../../services/lang-tgt/lang-tgt.service';
import {AccessService} from '../../../services/access/access.service';
import {PremiumModalComponent} from '../../../components/premium-modal/premium-modal.component';
import {LevelLockedModalComponent} from '../../../components/level-locked-modal/level-locked-modal.component';
import {CommonModule} from '@angular/common';
import {SpinnerComponent} from '../../../components/spinner/spinner.component';
import {TranslateModule} from '@ngx-translate/core';
import {ContentContextService} from '../../../services/content-context/content-context.service';
import {Subscription} from 'rxjs';
import {DeviceDetectorService} from 'ngx-device-detector';

@Component({
  selector: 'app-games-levels',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SpinnerComponent,
    RouterLink
  ],
  templateUrl: './games-levels.component.html',
  styleUrls: ['./games-levels.component.scss']
})
export class GamesLevelsComponent implements OnInit, OnDestroy {

  loading = true;
  idSection: string;
  data: any;
  isPremium = false;
  private paramsSub: Subscription;

  constructor(
    private route: ActivatedRoute,
    private modal: NgbModal,
    private router: Router,
    private gameLevelSrv: GameLevelService,
    public langSrcSrv: LangSrcService,
    public langTgtSrv: LangTgtService,
    private accessSrv: AccessService,
    private pageSrv: PageService,
    private metaSrv: MetaService,
    private translate: TranslateService,
    private contentContextSrv: ContentContextService,
    public deviceSrv: DeviceDetectorService
  ) {
  }

  async ngOnInit() {
    this.translate.get(`Chargement...`).subscribe((value: string) => {
      this.metaSrv.setMetas({title: value});
    });
    this.isPremium = await this.accessSrv.isPremium(this.langTgtSrv.current.code);
    // Le composant est réutilisé quand on navigue vers une autre section
    // (ex : carte « Prochaine étape ») : on recharge à chaque changement de paramètre
    this.paramsSub = this.route.params.subscribe(async (params) => {
      this.idSection = params.idSection;
      this.loading = true;
      await this.initialize();
      this.scrollToCurrent();
    });
  }

  async ngOnDestroy() {
    this.paramsSub?.unsubscribe();
    this.contentContextSrv.clear();
    this.pageSrv.displayPurpleMain = false;
  }

  async initialize() {
    this.data = await this.gameLevelSrv.findAllBySection(this.idSection);
    if (!this.data) {
      await this.router.navigate(['/', this.langSrcSrv.current.shortCode, 'games']);
      return;
    }
    this.metaSrv.setMetas({title: this.data.section.translation});
    this.contentContextSrv.set({type: 'section', items: [{id: this.data.section.uniqid, name: this.data.section.translation}]});
    this.pageSrv.displayPurpleMain = true;
    this.decorateLevels();
    this.loading = false;
  }

  // Prépare l'affichage : état de chaque niveau + pastilles de jeux
  private decorateLevels() {
    (this.data.levels ?? []).forEach((level: any, index: number) => {
      level.state = this.stepState(level, index);
      level.perfect = level.acquired && (level.bestScore ?? 0) >= 100;
      const total = level.games?.total ?? 0;
      const items = level.games?.items;
      if (items?.length) {
        // Détail réel jeu par jeu, dans l'ordre du niveau — toujours prioritaire,
        // y compris pour un niveau acquis via placement (chips à l'état réel)
        level.chips = items.map((item: any) =>
          item.state === 'passed' ? 'done' : (item.state === 'started' ? 'started' : 'todo'));
        level.gamesDone = level.games.passed ?? level.chips.filter((chip: string) => chip === 'done').length;
      } else {
        // Repli sans items : compteurs agrégés uniquement
        const done = level.games?.passed ?? 0;
        level.gamesDone = done;
        level.chips = [
          ...Array(done).fill('done'),
          ...Array(Math.max(total - done, 0)).fill('todo')
        ];
      }
    });
  }

  get hasStartedGames(): boolean {
    return (this.data?.levels ?? []).some((level: any) => level.chips?.includes('started'));
  }

  // Seuil de réussite d'un jeu (%), exposé par l'API au niveau de games.ratio
  get passRatio(): number {
    const levels = this.data?.levels ?? [];
    return levels.find((level: any) => level.games?.ratio)?.games.ratio ?? 0;
  }

  get firstLockedIndex(): number {
    return (this.data?.levels ?? []).findIndex((level: any) => level.locked);
  }

  // Le tronçon de ligne sous le nœud i est coloré si l'étape suivante est atteinte
  railDone(index: number): boolean {
    const levels = this.data?.levels ?? [];
    if (index + 1 < levels.length) return levels[index + 1].state !== 'locked';
    return this.endStepHighlighted;
  }

  // Centre l'écran sur le niveau en cours (ou remonte en haut s'il n'y en a pas)
  private scrollToCurrent() {
    setTimeout(() => {
      const container = document.querySelector('.body') as HTMLElement;
      if (!container) return;
      const el = document.querySelector('.gls-item--current') as HTMLElement;
      if (!el) {
        container.scrollTo({top: 0});
        return;
      }
      const elRect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const elTop = elRect.top - containerRect.top + container.scrollTop;
      container.scrollTo({
        top: elTop - container.clientHeight / 2 + el.offsetHeight / 2,
        behavior: 'smooth'
      });
    }, 300);
  }

  async skills() {
    const modal = this.modal.open(GamesSectionSkillsComponent, {
      size: 'lg',
      centered: true,
      scrollable: true
    });
    modal.componentInstance.section = this.data.section;
  }

  trackByIndex(index: number) { return index; }

  // ── Progression de la section (chemin de niveaux) ──────────

  // Niveau courant = dernier niveau déverrouillé non validé ; -1 si la section est terminée.
  // « Dernier » et pas « premier » : le placement peut débloquer plusieurs niveaux d'un coup
  // sans les marquer acquis — la position réelle est alors le plus avancé d'entre eux.
  get currentIndex(): number {
    const levels = this.data?.levels ?? [];
    let last = -1;
    levels.forEach((level: any, i: number) => {
      if (!level.locked && !level.acquired) last = i;
    });
    return last;
  }

  // La position réelle de l'utilisateur est-elle dans cette section ?
  // Si la suite du parcours est déjà débloquée alors que la section n'est pas terminée,
  // l'utilisateur a été placé plus loin (test de placement) : pas de niveau « en cours » ici.
  get isCurrentSection(): boolean {
    return !(this.data?.nextStep?.unlocked && !this.sectionDone);
  }

  stepState(level: any, index: number): 'completed' | 'validated' | 'current' | 'unlocked' | 'skipped' | 'locked' {
    if (level.locked) return 'locked';
    if (level.acquired) {
      // Acquis en jouant (tous les jeux réussis) vs acquis d'office par le
      // test de placement (skippé) : distinction visuelle « Réussi » / « Validé »
      const earned = !level.games?.total || (level.games.passed ?? 0) >= level.games.total;
      return earned ? 'completed' : 'validated';
    }
    if (this.isCurrentSection && index === this.currentIndex) return 'current';
    // Débloqué en amont de la position courante sans jamais avoir été joué :
    // sauté par le test de placement → en accès libre, pas de « 0/8, 0% » anxiogène
    const played = (level.games?.passed ?? 0) > 0 || (level.bestScore ?? 0) > 0;
    return played ? 'unlocked' : 'skipped';
  }

  get completedCount(): number {
    return (this.data?.levels ?? []).filter((level: any) => level.acquired).length;
  }

  get sectionDone(): boolean {
    const levels = this.data?.levels ?? [];
    return levels.length > 0 && levels.every((level: any) => level.acquired);
  }

  get sectionProgressPercent(): number {
    const levels = this.data?.levels ?? [];
    if (!levels.length) return 0;
    return Math.round((this.completedCount / levels.length) * 100);
  }

  // Prochaine étape accessible : le déblocage réel peut venir d'ailleurs que de cette section
  // (ex : test de placement) — on se fie à l'API si elle expose nextStep.unlocked
  get nextStepAccessible(): boolean {
    const next = this.data?.nextStep;
    if (!next) return false;
    return next.unlocked ?? this.sectionDone;
  }

  // Mise en avant (vert) de l'étape finale
  get endStepHighlighted(): boolean {
    return this.data?.nextStep ? this.nextStepAccessible : this.sectionDone;
  }

  // Lien de l'étape finale : la prochaine étape si elle est accessible, sinon le parcours
  get nextStepLink(): any[] {
    const lang = this.langSrcSrv.current.shortCode;
    const next = this.data?.nextStep;
    if (!next || !this.nextStepAccessible) return ['/', lang, 'games'];
    if (next.type === 'exam') return ['/', lang, 'cefrl-exam', next.uniqid];
    return ['/', lang, 'games', next.uniqid];
  }

  isPremiumLocked(levelIndex: number): boolean {
    return this.accessSrv.isLevelPremiumLocked(levelIndex, this.langTgtSrv.current, this.isPremium);
  }

  async navigateToLevel(level: any, levelIndex: number) {
    if (this.isPremiumLocked(levelIndex)) {
      this.modal.open(PremiumModalComponent, { centered: true });
      return;
    }
    if (level.locked) {
      this.modal.open(LevelLockedModalComponent, { centered: true });
      return;
    }
    await this.router.navigate(['/', this.langSrcSrv.current.shortCode, 'games', this.data.section.uniqid, level.uniqid]);
  }

}
